/**
 * SVG path for an orthogonal edge: vertical down, horizontal across,
 * vertical down again, with rounded corners at the two bends.
 *
 * Assumes a top-down layout (`targetY > sourceY`). When source and target
 * share an X, returns a straight vertical line with no corners.
 */

export interface OrthogonalEdgePathArgs {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  cornerRadius: number;
}

export function getOrthogonalEdgePath(args: OrthogonalEdgePathArgs): string {
  const { sourceX, sourceY, targetX, targetY, cornerRadius } = args;

  if (sourceX === targetX) {
    return `M ${sourceX} ${sourceY} V ${targetY}`;
  }

  const midY = (sourceY + targetY) / 2;
  const dx = targetX - sourceX;
  const dir = dx > 0 ? 1 : -1;

  // Clamp the radius so each arc fits inside its trunk, arm, and drop.
  // Without this, a tight horizontal or vertical would push an arc past the
  // segment it's supposed to round.
  const r = Math.min(
    cornerRadius,
    Math.abs(dx) / 2,
    midY - sourceY,
    targetY - midY
  );

  // Sweep flags. SVG sweep=1 is clockwise on screen. Going right: the
  // down→right bend is CCW (the arc bows into the upper-right of its
  // bounding box — short way is sweep 0), and the right→down bend is CW
  // (sweep 1). Going left, both senses invert.
  const sweep1 = dir > 0 ? 0 : 1;
  const sweep2 = dir > 0 ? 1 : 0;

  return [
    `M ${sourceX} ${sourceY}`,
    `V ${midY - r}`,
    `A ${r} ${r} 0 0 ${sweep1} ${sourceX + r * dir} ${midY}`,
    `H ${targetX - r * dir}`,
    `A ${r} ${r} 0 0 ${sweep2} ${targetX} ${midY + r}`,
    `V ${targetY}`,
  ].join(' ');
}
